// © Microsoft Corporation. All rights reserved.

import { ActivityLoggingService } from "clientcore-infrastructure-analytics/ActivityLoggingService";
import { BrowserConsoleAppender } from "clientcore-infrastructure-analytics/appenders/BrowserConsoleAppender";
import { ExponentialBackoffRetry } from "@msnews/core";
import { IInstrumentationService } from "clientcore-infrastructure-analytics/IInstrumentationService";
import { InstrumentationService } from "clientcore-infrastructure-analytics/InstrumentationService";
import { LogLevel } from "clientcore-infrastructure-analytics/models/LogLevel";
import { OneServiceUtility } from "./OneServiceUtility";
import { canUseDOM } from "@msnews/isomorphic-util";
import { logger } from "@msnews/diagnostics";

/**
 * Instrumentation service
 *
 * @type {IInstrumentationService}
 */
let instrumentationService: IInstrumentationService;

/**
 * Retry manager
 *
 * @type {ExponentialBackoffRetry}
 */
let exponentialBackoffRetry: ExponentialBackoffRetry;

/**
 * Number of times to retry requests
 */
const retryCount = 3;

/**
 * Sends a request
 * If the request utilizes AbortController, provide the controller instance to abort the fetch with ExponentialBackoffRetry
 * @returns {Promise<Response>} - A promise to fulfill the request
 */
export async function sendRequest(request: () => Promise<Response>, activityName: string): Promise<Response> {
    const activity = getInstrumentationService().activityLoggingService.createActivity(activityName);
    return await OneServiceUtility.serviceRequestOnComplete(
        () => getExponentialBackoffRetry().executeRequest(request, null, activity),
        () => getInstrumentationService().activityLoggingService.endActivity(activity));
}

/**
 * Get exponential backoff retry instance
 */
function getExponentialBackoffRetry(): ExponentialBackoffRetry {
    if (exponentialBackoffRetry) {
        return exponentialBackoffRetry;
    }

    // On server side retryCount wil be ignored
    exponentialBackoffRetry = new ExponentialBackoffRetry(getInstrumentationService().loggingService, retryCount);

    return exponentialBackoffRetry;
}

/**
 * Get instrumentation service instance
 */
function getInstrumentationService(): IInstrumentationService {
    if (instrumentationService) {
        return instrumentationService;
    }

    const activityService = ActivityLoggingService.getInstance("ExperienceActivityLogger");
    // Todo: use a different appender to log into mds tables
    if (canUseDOM() && logger.isDebug()) {
        activityService.addAppender(new BrowserConsoleAppender(LogLevel.Activity));
    }

    instrumentationService = new InstrumentationService(logger.getLoggingService(), activityService);

    return instrumentationService;
}
